/**
 * The chat wire protocol: what the server sends and the widget reads.
 *
 * Deliberately free of anything node-shaped, because the widget runs in a
 * browser and `sse.ts` refers to `NodeJS.Timeout`. The shared vocabulary lives
 * here and is re-exported both ways (`@rag/channels` for the server,
 * `@rag/channels/protocol` for the browser bundle), so client and server
 * cannot drift apart.
 */
export const SSE_EVENTS = {
    /** First frame. Carries the ids the widget needs for later turns. */
    open: 'open',
    /** Coarse progress so the widget can show something other than a spinner. */
    status: 'status',
    /**
     * A piece of the answer, as the model writes it.
     *
     * Optional in both directions. The server sends these only when the model
     * provider can stream; a client that ignores them still gets the whole
     * answer in the `message` frame, so older deployed widgets keep working.
     *
     * The text is guarded but NOT channel-formatted; see `DeltaFrame`.
     */
    delta: 'delta',
    /** The answer, formatted for the web channel. */
    message: 'message',
    /**
     * The lead journey's chips and enrolment link for this turn.
     *
     * Optional in both directions, like `delta`. Sent after `message` when the
     * journey has something to offer; a client that ignores it still gets a
     * complete answer, so older deployed widgets keep working.
     *
     * After `message` deliberately: the answer is what the visitor asked for and
     * the chips are what to do next, so a client rendering frames as they arrive
     * paints them in that order.
     */
    journey: 'journey',
    /** A human took the conversation over; the bot stays quiet. */
    handoff: 'handoff',
    /** Terminal failure. Always followed by `done`. */
    error: 'error',
    /** Last frame, success or not. The widget closes the reader on this. */
    done: 'done',
};
/**
 * The dashboard's own stream.
 *
 * A separate vocabulary from `SSE_EVENTS`: one stream carries a single answer
 * to an anonymous visitor and ends, the other fans out conversation events to
 * an employee for hours. One union would let a widget handler be written
 * against a `handoff.taken` frame it can never receive.
 *
 * `SseStream` accepts either, since it is the transport for both.
 */
export const DASHBOARD_SSE_EVENTS = {
    /** First frame. Says whether events will actually arrive; see the stream route. */
    ready: 'ready',
    /** A message was stored. Ids only; the client refetches over the API. */
    message: 'message',
    /** A conversation changed status: takeover, release, close, flag. */
    conversation: 'conversation',
    /** The bot asked for a human. Drives the queue badge and the toast. */
    handoff: 'handoff',
};
export const CHANNEL_CAPABILITIES = {
    WEB: { richBlocks: true, quickReplies: 4, forms: true },
    WHATSAPP: { richBlocks: false, quickReplies: 3, forms: false },
};
/**
 * Messages between the loader (host page) and the iframe.
 *
 * The box has a fixed size per state (the character alone when closed, the
 * character plus the docked panel when open, the viewport in mobile
 * fullscreen), so the frame only reports *which* state it is in, and the host
 * sizes the
 * box. Nothing depends on measuring content.
 */
export const WIDGET_MESSAGES = {
    /** Frame → host. Booted; the launcher can be shown. */
    ready: 'rag:ready',
    /** Frame → host. The visitor opened the panel. */
    open: 'rag:open',
    /** Frame → host. The visitor closed the panel from inside the frame. */
    close: 'rag:close',
    /** Host → frame. Where the character sits, and whether the panel is fullscreen. */
    layout: 'rag:layout',
    /**
     * Host → frame. The cursor, normalised to −1..1 against the host window and
     * measured from the character's centre, so the head can follow it across
     * the whole page, not only over the iframe.
     */
    pointer: 'rag:pointer',
    /** Frame → host. Bring the character back to centre (the header's expand control, or a double-click in a corner). */
    recenter: 'rag:recenter',
    /** Frame → host. The header's collapse control, centred: move aside to the corner, as "apartar" does. */
    dock: 'rag:dock',
    /** Host → frame. The invitation card was pressed; open the panel. */
    requestOpen: 'rag:request-open',
    /** Host → frame. An invitation shortcut was pressed; open and ask `text`. */
    ask: 'rag:ask',
    /**
     * Host → frame. The invitation's "Cursos" was pressed: open, and start at the
     * course categories (after the privacy notice, if it is still due).
     */
    startCourses: 'rag:start-courses',
};
//# sourceMappingURL=protocol.js.map